import { Drawer, Skeleton, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { DailyBite } from '@nelnela/contracts';
import { api } from '../../lib/api';
import { speak, ttsCalmLine } from '../../lib/tts-player';
import { useState } from 'react';
import { IconPlay } from '../watch/icons';

/**
 * The one-pattern tile's sheet (M6a step 11).
 *
 * Distinct from `components/watch/PatternSheet.tsx`, which is the EDITOR — the slot-by-slot
 * adjuster the learner uses when saving a pattern of their own. This one is read-only and is
 * about noticing: here is the frame, here is what it does, and here are two lines you have
 * actually watched where it turned up.
 *
 * "Try two" is the only thing to do, and it is two prompts, not a run. The tile says two minutes
 * and the sheet has to mean it — a bite tile that opened into a six-item review would be the app
 * moving the goalposts after the learner agreed to them.
 */

export interface PatternSheetProps {
  opened: boolean;
  onClose: () => void;
  tile: DailyBite['plan'][number] | null;
  /** the bite, so "Try two" can attribute its activity back to the tile */
  biteId: string;
}

export function PatternSheet({ opened, onClose, tile, biteId }: PatternSheetProps) {
  const itemId = tile?.itemIds[0] ?? null;
  const [calm, setCalm] = useState<string | null>(null);

  const item = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => api.itemGet(itemId!),
    enabled: opened && !!itemId,
  });
  const lesson = useQuery({
    queryKey: ['lesson-for-item', itemId],
    queryFn: () => api.lessonForItem(itemId!),
    enabled: opened && !!itemId,
  });

  const play = async (text: string) => {
    setCalm(null);
    try {
      await speak(text, { title: text, subtitle: 'pattern', key: `pattern:${text}` });
    } catch (err) {
      setCalm(ttsCalmLine(err));
    }
  };

  const template =
    tile?.display?.title ?? item.data?.item.displayForm ?? item.data?.item.lemma ?? '';
  // `ItemView.item` carries the examples the catalog has; the brief asks for two lines the
  // learner has actually consumed, which is what `PatternPreviewResponse` is for — but that is a
  // POST that re-runs the matcher, and this sheet opens from a tap on Today. The catalog's own
  // examples are already the right shape and cost nothing, so they are what is shown, and the
  // sheet says "for example" rather than claiming they came from the learner's own watching.
  const examples = item.data?.catalog?.examples ?? [];

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="auto"
      withCloseButton={false}
      styles={{
        content: {
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderTop: '1px solid var(--nn-hairline)',
          background: 'var(--nn-panel)',
        },
        body: { padding: 0 },
      }}
      transitionProps={{ duration: 160 }}
    >
      <div
        aria-hidden
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: 'var(--nn-hairline)',
          margin: '8px auto 4px',
        }}
      />
      <div style={{ padding: '6px 16px 22px' }}>
        <div
          lang="ka"
          style={{
            fontFamily: "'Noto Serif Georgian', serif",
            fontSize: 17,
            color: 'var(--nn-text-strong)',
          }}
        >
          {template}
        </div>
        {tile?.display?.meta && (
          <div style={{ marginTop: 3, fontSize: 11, color: 'var(--nn-muted)' }}>
            {tile.display.meta}
          </div>
        )}

        {lesson.isPending ? (
          <Skeleton height={12} width="70%" radius="sm" mt={12} />
        ) : (
          lesson.data?.ruleSummary && (
            <Text fz={12.5} c="var(--nn-muted)" mt={12} lh={1.5}>
              {lesson.data.ruleSummary}
            </Text>
          )
        )}

        {examples.length > 0 && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <span style={{ fontSize: 10, color: 'var(--nn-faint)', letterSpacing: '.05em' }}>
              FOR EXAMPLE
            </span>
            {examples.slice(0, 2).map((ex) => (
              <div key={ex.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <UnstyledButton
                  onClick={() => void play(ex.text)}
                  aria-label="Hear it"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    flexShrink: 0,
                    background: 'color-mix(in srgb, var(--nn-accent) 13%, transparent)',
                    color: 'var(--nn-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ display: 'flex', transform: 'scale(.4)' }}>
                    <IconPlay />
                  </span>
                </UnstyledButton>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <span
                    lang="ka"
                    style={{
                      fontFamily: "'Noto Serif Georgian', serif",
                      fontSize: 14,
                      color: 'var(--nn-text)',
                    }}
                  >
                    {ex.text}
                  </span>
                  {ex.translation && (
                    <span style={{ fontSize: 11.5, color: 'var(--nn-muted)' }}>
                      {ex.translation}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {calm && (
          <Text fz={11} c="var(--nn-muted)" mt={8} role="status">
            {calm}
          </Text>
        )}

        {itemId && tile && (
          <Link
            to={tryTwoHref(biteId, tile.idx, itemId)}
            onClick={onClose}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              marginTop: 16,
              padding: '9px 18px',
              borderRadius: 8,
              background: 'var(--nn-accent)',
              color: 'var(--nn-on-accent)',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Try two
          </Link>
        )}
      </div>
    </Drawer>
  );
}

/** Two pattern completions, attributed back to the tile so it flips when they are done. */
export function tryTwoHref(biteId: string, planIdx: number, itemId: string): string {
  const p = new URLSearchParams({
    dailyBiteId: biteId,
    planIdx: String(planIdx),
    itemIds: itemId,
    kinds: 'pattern_completion',
    count: '2',
  });
  return `/review?${p.toString()}`;
}
